import { Injectable } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { CustomerService } from './customer/customer.service';
import { DoorService } from './door/door.service';
import { ScenarioImageService } from './scenario-image/scenario-image.service';
import { UserService } from './user/user.service';
import {BackendTrackingService} from "./backend-tracking/backend-tracking.service";
import {CustomerServiceImpl} from "./customer/customer.service.impl";

@Injectable({ providedIn: 'root' })
export class TrackingService {

  private static get windowRef(): any {
    return window;
  }

  private gtmTagIds: { [key: string]: string } = {
    'overheaddoor': 'GTM-WNJD28FW',
    'wayne-dalton': 'GTM-KDDZ9ZJ',
    'demo': 'GTM-WLTK8QM'
  }

  constructor(private customerService: CustomerService,
              private doorService: DoorService,
              private scenarioService: ScenarioImageService,
              private backendTrackingService: BackendTrackingService,
              private userService: UserService) {

    if (environment.trackingEnabled) {
      this.loadGTM()
    }
  }

  private loadGTM(): void {
    const customerKey = CustomerServiceImpl.GetCustomerKey().toLowerCase();

    this.customerService.getCustomerConfig$().subscribe(customerConfig => {
      const gtmId = customerConfig?.gtmTag || this.gtmTagIds[customerKey];
      if (!gtmId) {
        return;
      }

      // Check if GTM script is already added
      if (document.getElementById('GTMScript')) {
        return;
      }

      // Creates official GTM script with injected env vars
      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.id = 'GTMScript';

      TrackingService.windowRef.dataLayer = TrackingService.windowRef.dataLayer || [];
      TrackingService.windowRef.dataLayer.push({ event: 'gtm.js', 'gtm.start': new Date().getTime() });

      gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=' + gtmId;

      document.head.insertBefore(gtmScript, document.head.firstChild);

      // Add noscript fallback just after the opening <body> tag
      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(noscript, document.body.firstChild);
    })
  }

  /**
   * tracks given event with given data body
   * @param event name of event to track
   * @param body additional information to track
   * <p> if not set, doorId, externalDoorId, scenarioId, scenarioType, userId and customerKey will be automatically added to body
   */
  async track({ event, body }: { event: string, body?: any }): Promise<void> {

    if (!body) {
      body = {} as any;
    }

    if (!body.doorId) {
      const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex()

      if (currentDoor) {
        body.doorId = currentDoor.id;
        body.externalDoorId = currentDoor.externalIds[0];
      }
    }

    if (!body.scenarioId) {
      const currentScenario = await lastValueFrom(this.scenarioService.getSelected$().pipe(take(1)));
      body.scenarioId = currentScenario.id;
      body.scenarioType = currentScenario.type;
    }

    if (!body.userId) {
      body.userId = await this.userService.getUserId()
    }

    if (!body.customerKey) {
      body.customerKey = CustomerServiceImpl.GetCustomerKey();
    }

    const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex()

    try {
      this.backendTrackingService.trackEvent({
        customerKey: body.customerKey,
        scenarioId: body.scenarioId,
        scenarioType: body.scenarioType,
        appUserShortId: body.userId,
        productExternalId: currentDoor?.externalIds[0],
        name: event
      }).then();
    } catch (e) {
      console.trace(`Error during event tracking: ${e}`)
    }

    if (!environment.trackingEnabled) {
      return;
    }

    const dataLayer = TrackingService.windowRef.dataLayer || [];

    // to reset all body parameters, so data "leak" between events
    dataLayer.push(function(this: any) {
      this.set('body', undefined);
    });
    return dataLayer.push({ event: event, body });
  }
}
